import { useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { CheckCircle, XCircle, Loader2 } from "lucide-react";
import PageSEO from "@/components/PageSEO";
import { useTranslation } from "react-i18next";
import { useAITranslate } from "@/hooks/useAITranslate";

const SUPABASE_URL = import.meta.env.VITE_SUPABASE_URL;
const SUPABASE_KEY = import.meta.env.VITE_SUPABASE_PUBLISHABLE_KEY;

type Status = "loading" | "valid" | "already" | "invalid" | "success" | "error";

const Unsubscribe = () => {
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  const [status, setStatus] = useState<Status>("loading");
  const { i18n } = useTranslation();
  const lang = (i18n.resolvedLanguage || i18n.language || "fr").slice(0, 2);
  const [
    tSeoTitle,
    tSeoDesc,
    tHeading,
    tConfirmPrompt,
    tConfirmBtn,
    tSuccess,
    tAlready,
    tInvalid,
    tError,
  ] = useAITranslate([
    "Désabonnement — Maroc Transfers",
    "Gérez votre désabonnement des communications email de Maroc Transfers en un clic.",
    "Gérer votre désabonnement",
    "Souhaitez-vous vous désabonner des emails de Maroc Transfers ?",
    "Confirmer le désabonnement",
    "Vous avez été désabonné avec succès.",
    "Vous êtes déjà désabonné.",
    "Lien invalide ou expiré.",
    "Une erreur est survenue. Veuillez réessayer.",
  ]);

  useEffect(() => {
    if (!token) { setStatus("invalid"); return; }
    fetch(`${SUPABASE_URL}/functions/v1/handle-email-unsubscribe?token=${token}`, {
      headers: { apikey: SUPABASE_KEY },
    })
      .then((r) => r.json())
      .then((d) => {
        if (d.valid === false && d.reason === "already_unsubscribed") setStatus("already");
        else if (d.valid) setStatus("valid");
        else setStatus("invalid");
      })
      .catch(() => setStatus("invalid"));
  }, [token]);

  const handleUnsubscribe = async () => {
    setStatus("loading");
    try {
      const { data } = await supabase.functions.invoke("handle-email-unsubscribe", {
        body: { token },
      });
      if (data?.success) setStatus("success");
      else if (data?.reason === "already_unsubscribed") setStatus("already");
      else setStatus("error");
    } catch { setStatus("error"); }
  };

  return (
    <div className="min-h-screen bg-background flex items-center justify-center p-6" lang={lang}>
      <PageSEO
        title={tSeoTitle}
        description={tSeoDesc}
        path="/unsubscribe"
        noindex
      />
      <div className="max-w-md w-full text-center space-y-6">
        <h1 className="font-heading text-3xl font-bold text-foreground">{tHeading}</h1>

        {status === "loading" && <Loader2 className="w-8 h-8 animate-spin mx-auto text-primary" />}

        {status === "valid" && (
          <>
            <p className="text-muted-foreground">{tConfirmPrompt}</p>
            <Button onClick={handleUnsubscribe} className="bg-gradient-gold text-primary-foreground">
              {tConfirmBtn}
            </Button>
          </>
        )}

        {status === "success" && (
          <div className="space-y-3">
            <CheckCircle className="w-12 h-12 text-green-500 mx-auto" />
            <p className="text-foreground font-medium">{tSuccess}</p>
          </div>
        )}

        {status === "already" && (
          <div className="space-y-3">
            <CheckCircle className="w-12 h-12 text-muted-foreground mx-auto" />
            <p className="text-muted-foreground">{tAlready}</p>
          </div>
        )}

        {status === "invalid" && (
          <div className="space-y-3">
            <XCircle className="w-12 h-12 text-destructive mx-auto" />
            <p className="text-muted-foreground">{tInvalid}</p>
          </div>
        )}

        {status === "error" && (
          <div className="space-y-3">
            <XCircle className="w-12 h-12 text-destructive mx-auto" />
            <p className="text-muted-foreground">{tError}</p>
          </div>
        )}
      </div>
    </div>
  );
};

export default Unsubscribe;
